import type { ComponentProps, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading03Icon, Tick02Icon } from '@hugeicons/core-free-icons'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import HumblyticsLogo from '@/assets/humblytics-web-app-logo.svg'

/**
 * Paper "03 - App · System": brand row on the canvas, one 440px raised card
 * centred below it. Shared by MCP connect, connector OAuth return, checkout,
 * agent hand-offs, invites and the 404 so every one-off screen reads the same.
 */
export function SystemCardShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="flex min-h-svh flex-col bg-canvas text-foreground">
      <header className="flex h-[72px] shrink-0 items-center px-4 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src={HumblyticsLogo} alt="" className="size-5" />
          <span className="text-base font-medium leading-5 tracking-[-0.01em]">Humblytics</span>
        </Link>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 pb-[72px] sm:px-8">
        <div className={cn('surface-raised flex w-full max-w-[440px] flex-col gap-7 px-5 py-7 sm:p-10', className)}>
          {children}
        </div>
      </main>
    </div>
  )
}

export function SystemCardHeading({
  eyebrow,
  title,
  children,
  success,
}: {
  eyebrow: ReactNode
  title: ReactNode
  children?: ReactNode
  success?: boolean
}) {
  const text = (
    <div className="flex flex-col gap-2">
      <p className="text-[13px] font-medium leading-5 text-muted-foreground">{eyebrow}</p>
      <h1 className="text-balance text-[22px] font-semibold leading-7 tracking-[-0.02em] sm:text-[28px] sm:leading-[34px]">
        {title}
      </h1>
      {children && <div className="text-sm leading-[22px] text-muted-foreground">{children}</div>}
    </div>
  )
  if (!success) return text
  return (
    <div className="flex flex-col gap-4">
      <div className="flex size-10 items-center justify-center rounded-full bg-success-tint">
        <HugeiconsIcon icon={Tick02Icon} size={20} strokeWidth={2.4} className="text-success" />
      </div>
      {text}
    </div>
  )
}

export function SystemCardActions({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2">{children}</div>
}

const ACTION_BASE = 'h-11 w-full rounded-lg px-4 text-sm font-medium shadow-none'

export function SystemCardPrimary(props: ComponentProps<typeof Button>) {
  return <Button {...props} className={cn(ACTION_BASE, 'bg-clip-border', props.className)} />
}

export function SystemCardSecondary(props: ComponentProps<typeof Button>) {
  return (
    <Button
      variant="outline"
      {...props}
      className={cn(ACTION_BASE, 'border-input bg-card text-foreground hover:bg-muted/60 hover:text-foreground', props.className)}
    />
  )
}

export function SystemCardProgress({ label }: { label: string }) {
  return (
    <div role="status" className="flex h-11 items-center justify-center gap-2 rounded-lg bg-canvas">
      <HugeiconsIcon icon={Loading03Icon} size={16} className="animate-spin text-foreground" />
      <span className="text-[13px] font-medium leading-5 text-muted-foreground">{label}</span>
    </div>
  )
}

export function SystemCardFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('border-t border-border pt-5 text-xs leading-[18px] text-muted-foreground', className)}>
      {children}
    </div>
  )
}
